import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";

import config from "../../../../config";
import { useRegionView } from "../../../../services/useRegionView";

const ComparisonDashboard = () => {
  const API_TOKEN = config.MAP_MATCHING_API_TOKEN;
  const API_URL = config.MAP_MATCHING_API_URL;
  const { island_id } = useRegionView();

  const currentYear = new Date().getFullYear();

  const yearOptions = useMemo(() => {
    const years = [];
    // adjust start year if you want
    for (let y = 2018; y <= currentYear; y += 1) years.push(y);
    return years;
  }, [currentYear]);

  // default: last 2 years (safe)
  const [selectedYears, setSelectedYears] = useState(() => {
    const y1 = currentYear - 1;
    const y2 = currentYear;
    return yearOptions.includes(y1) ? [y1, y2] : [yearOptions.at(-2), yearOptions.at(-1)].filter(Boolean);
  });

  const [loading, setLoading] = useState(false);

  // API response shape:
  // { stats: [{title,line1,line2,line3},...], diagrams: [{labels, datasets:[{label,data,color}]}...] }
  const [stats, setStats] = useState([]);
  const [diagrams, setDiagrams] = useState([]);

  const yearsParam = useMemo(() => {
    // keep stable order + remove duplicates
    const uniqSorted = Array.from(new Set(selectedYears))
      .filter((y) => Number.isFinite(Number(y)))
      .sort((a, b) => Number(a) - Number(b));
    return uniqSorted.join(",");
  }, [selectedYears]);

  const canFetch = selectedYears?.length >= 1 && yearsParam.length > 0;

  useEffect(() => {
    // initial load
    fetchComparisonData({ forceIsland100: true });

  }, []);

  const fetchComparisonData = async ({ forceIsland100 = false } = {}) => {
    if (!canFetch) return;

    setLoading(true);
    try {
      const params = {
        years: yearsParam, // "2020,2021"
      };

      // follow your app pattern: island_id from region view (skip if null/0)
      if (forceIsland100) {
        params.island_id = 100;
      } else {
        if (island_id != null && island_id !== 0) {
          params.island_id = island_id;
        }
      }

      const response = await axios.get(`${API_URL}/statistics/comparisons`, {
        params,
        headers: {
          "X-API-Key": API_TOKEN,
          accept: "*/*",
        },
      });

      const data = response.data;

      if (Array.isArray(data?.stats)) setStats(data.stats);
      else setStats([]);

      if (Array.isArray(data?.diagrams)) setDiagrams(data.diagrams);
      else setDiagrams([]);
    } catch (e) {
      console.error("Failed to fetch comparisons statistics", e);
      setStats([]);
      setDiagrams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFetch = async () => {
    await fetchComparisonData({ forceIsland100: false });
  };

  // Titles based on your sample response (month/day/hour)
  const diagramTitles = useMemo(
    () => [
      { title: "Μέση Κατανομή ανά Μήνα", description: "Όλων των επιλεγμένων ετών" },
      { title: "Μέση Κατανομή ανά Ημέρα", description: "Όλων των επιλεγμένων ετών" },
      { title: "Μέση Κατανομή ανά Ώρα", description: "Όλων των επιλεγμένων ετών" },
    ],
    []
  );

const StatSquare = ({ title, line1 }) => {
  const formatNumber = (value) => {
    const num = Number(value);
    if (isNaN(num)) return value;
    return num.toFixed(2);
  };

  return (
    <Card
      sx={{
        height: { xs: 170, sm: 180, md: 170 },
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.12)",
        overflow: "hidden",
      }}
    >
      <MDBox
        sx={{
          height: "100%",
          p: 2.5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Title */}
        <MDTypography
          variant="button"
          fontWeight="bold"
          sx={{ mb: 1 }}
        >
          {title}
        </MDTypography>

        {/* Centered Big Number */}
        <MDBox
          sx={{
            flexGrow: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MDTypography
            variant="h3"
            fontWeight="bold"
            color="info"
          >
            {formatNumber(line1)}
          </MDTypography>
        </MDBox>
      </MDBox>
    </Card>
  );
};

  return (
    <DashboardLayout>
      <MDBox mt={4} p={0}>
        <Grid container spacing={3}>
          {/* Header + Filters */}
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
              <MDBox p={3}>
                <MDTypography variant="h5" sx={{ lineHeight: 1.3, maxWidth: "900px" }}>
                Μέσοι Όροι Βασικών Αριθμών εκροής 
                </MDTypography>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2} alignItems="center">
                  {/* Years multi-select */}
                  <Grid item xs={12} md={8}>
                    <MDTypography
                      component="label"
                      variant="button"
                      fontWeight="regular"
                      color="text"
                      sx={{ mb: 1, mr: 1 }}
                    >
                      Έτη (πολλαπλή επιλογή)
                    </MDTypography>

                    <Autocomplete
                      multiple
                      options={yearOptions}
                      value={selectedYears}
                      onChange={(_, newValue) => setSelectedYears(newValue)}
                      disableCloseOnSelect
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          placeholder="Επίλεξε έτη"
                        />
                      )}
                    />

                    <MDBox mt={1}>
                      <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                        Επιλεγμένα: {yearsParam || "—"}
                      </MDTypography>
                    </MDBox>
                  </Grid>

                  {/* Update button */}
                  <Grid
                    item
                    xs={12}
                    md={4}
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", md: "flex-end" },
                      alignItems: "flex-end",
                    }}
                  >
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={handleFetch}
                      disabled={loading || !canFetch}
                      sx={{ "&.MuiButton-root": { textTransform: "none" } }}
                    >
                      {loading ? "Φόρτωση..." : "Ενημέρωση Στατιστικών"}
                    </MDButton>
                  </Grid>

                  {/* Tip */}
                  <Grid item xs={12}>
                    <MDBox
                      sx={{
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      <MDTypography variant="button" color="text" sx={{ opacity: 0.85 }}>
                        Tip: Επίλεξε 2+ έτη για πιο χρήσιμη ανάλυση.
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {/* Stats squares (like GeneralStatistics) */}
          {stats.map((s, idx) => (
            <Grid key={idx} item xs={12} sm={6} md={3}>
              <StatSquare title={s.title} line1={s.line1} />
            </Grid>
          ))}

          {/* Diagrams (like CompareDashboard) */}
          <Grid item xs={12}>
            <Card sx={{ overflow: "visible" }}>
                      {/* Section Title */}

                                    <MDBox p={3}>
                <MDTypography variant="h5" sx={{ lineHeight: 1.3, maxWidth: "900px" }}>
                 Διαγράμματα Χρονικής Κατανομής Tροχαίων Aτυχημάτων
                </MDTypography>

                <Divider sx={{ my: 2 }} />
                </MDBox>
              <MDBox p={3}>
                <Grid container spacing={3}>
                  {diagrams.map((chart, idx) => (
                    <Grid key={idx} item xs={12} md={6}>
                      <MDBox px={{ xs: 1, md: 2 }}>
                        <GradientLineChart
                          icon={{ color: "dark", component: "leaderboard" }}
                          title={diagramTitles[idx]?.title || `Διάγραμμα ${idx + 1}`}
                          description={diagramTitles[idx]?.description || ""}
                          chart={chart}
                        />
                      </MDBox>
                    </Grid>
                  ))}

                  {!loading && diagrams.length === 0 && (
                    <Grid item xs={12}>
                      <MDBox px={{ xs: 1, md: 2 }}>
                        <MDTypography variant="button" color="text" sx={{ opacity: 0.8 }}>
                          Δεν υπάρχουν διαθέσιμα διαγράμματα για τη συγκεκριμένη επιλογή.
                        </MDTypography>
                      </MDBox>
                    </Grid>
                  )}
                </Grid>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
};

export default ComparisonDashboard;
